// Visibility evaluator for FIR wizard.
// Evaluates Deluge-like conditions ("Field_name == 'Oui'", with ||, &&, .contains())
// against the current answers map.

function safeEvalCondition(condStr, answers) {
  // Replace `Field_name.contains("xxx")` -> `__contains(Field_name, "xxx")`
  let s = condStr;
  s = s.replace(/([A-Za-z_][A-Za-z0-9_]*)\.contains\(/g, '__contains($1,');
  // Replace `X is not null` / `X is null` with helper forms (Deluge syntax)
  s = s.replace(/([A-Za-z_][A-Za-z0-9_]*)\s+is\s+not\s+null/gi, '__notempty($1)');
  s = s.replace(/([A-Za-z_][A-Za-z0-9_]*)\s+is\s+null/gi, '__empty($1)');
  // Replace remaining bare field references with __ans('Name')
  // Only replace tokens that are identifiers and aren't followed by '(' (function call)
  // We tokenize manually to avoid breaking strings.
  let out = '';
  let i = 0;
  while (i < s.length) {
    const ch = s[i];
    // String literal — copy as-is
    if (ch === '"' || ch === "'") {
      const quote = ch;
      out += ch;
      i++;
      while (i < s.length && s[i] !== quote) {
        if (s[i] === '\\' && i + 1 < s.length) { out += s[i] + s[i + 1]; i += 2; continue; }
        out += s[i]; i++;
      }
      if (i < s.length) { out += s[i]; i++; }
      continue;
    }
    // Identifier
    if (/[A-Za-z_]/.test(ch)) {
      let j = i;
      while (j < s.length && /[A-Za-z0-9_]/.test(s[j])) j++;
      const ident = s.substring(i, j);
      // Skip rewriting our helper names and JS keywords
      if (ident === '__contains' || ident === '__ans' ||
          ident === '__notempty' || ident === '__empty' ||
          ident === 'true' || ident === 'false' || ident === 'null') {
        out += ident;
      } else {
        // If followed by '(' it's a function call -> leave as-is
        if (s[j] === '(') {
          out += ident;
        } else {
          out += `__ans(${JSON.stringify(ident)})`;
        }
      }
      i = j;
      continue;
    }
    out += ch;
    i++;
  }

  const __ans = (name) => {
    const v = answers ? answers[name] : undefined;
    return v == null ? '' : v;
  };
  const __contains = (val, needle) => {
    if (Array.isArray(val)) return val.includes(needle);
    if (typeof val === 'string') return val.includes(needle);
    return false;
  };
  const __empty = (val) => {
    if (val == null) return true;
    if (Array.isArray(val)) return val.length === 0;
    if (typeof val === 'string') return val.trim() === '';
    return false;
  };
  const __notempty = (val) => !__empty(val);
  try {
    // eslint-disable-next-line no-new-func
    const fn = new Function('__ans', '__contains', '__empty', '__notempty',
      `return (${out});`);
    return !!fn(__ans, __contains, __empty, __notempty);
  } catch {
    return false;
  }
}

/**
 * Compute the set of visible field names given answers, default_hidden list and rules.
 * Strategy:
 *   - Start with all-visible.
 *   - Hide every name in default_hidden.
 *   - For each rule whose condition is true, apply its show/hide actions.
 */
export function computeVisibility(schema, answers) {
  const visibility = {}; // name -> bool
  // initialise with every field in the schema visible
  schema.sections.forEach(sec => {
    visibility[sec.name] = true;
    sec.fields.forEach(f => { visibility[f.name] = true; });
  });
  // apply default hide
  (schema.default_hidden || []).forEach(name => { visibility[name] = false; });
  // apply rules in order
  (schema.rules || []).forEach(rule => {
    if (safeEvalCondition(rule.condition, answers)) {
      (rule.show || []).forEach(n => { visibility[n] = true; });
      (rule.hide || []).forEach(n => { visibility[n] = false; });
    }
  });
  return visibility;
}

/**
 * Build a flat sequence of [{type:'section'|'field', section, field}] in display order.
 * Sections appear before their first visible field (so we can show a section title).
 */
export function buildVisibleSequence(schema, answers) {
  const vis = computeVisibility(schema, answers);
  const seq = [];
  schema.sections.forEach(section => {
    if (vis[section.name] === false) return; // section masquée par condition
    const visibleFields = section.fields.filter(f => vis[f.name] !== false);
    // Filter out non-input field types
    const inputFields = visibleFields.filter(f => isInputField(f));
    if (inputFields.length === 0) return; // skip empty sections
    seq.push({ type: 'section', section });
    inputFields.forEach(field => {
      seq.push({ type: 'field', section, field });
    });
  });
  return seq;
}

export function isInputField(f) {
  const skipTypes = new Set([
    'submit', 'reset', 'cancel', 'tooltip', 'plaintext',
    'help_text', 'prefix', 'suffix', 'richtext'
  ]);
  if (!f.type) return false;
  return !skipTypes.has(f.type);
}
